// =====================================================================
// SearchableSelect.jsx - Search-as-you-type select (M-004/005 B2.2)
// =====================================================================
// Componenta reutilizabila pentru dropdown cu search.
// Folosit in:
//   - ComptageFormModal (items)
//   - MouvementFormModal (carriers OR projects, depinde de recipientType)
//   - ItemFormModal (projects)
//
// Props:
//   items:        Array<Object> - lista completa
//   selected:     Object|null   - itemul selectat (sau null)
//   onSelect:     fn(item)      - callback la selectare (sau null pt deselect)
//   disabled:     bool
//   placeholder:  string        - text input gol
//   getKey:       fn(item) => string  - extrage id pentru key
//   getPrimary:   fn(item) => string  - text principal afisat (ex name)
//   getSecondary: fn(item) => string  - text secundar afisat (ex ref + projet)
//   searchFields: Array<fn(item) => string>  - functii pentru a extrage texte pentru search
//   maxResults:   number (default 8)
//   autoFocus:    bool
// =====================================================================

import { useState, useEffect, useRef } from 'react';

const inputStyle = {
  width: '100%',
  padding: '0.5rem 0.625rem',
  border: '1px solid #d1d5db',
  borderRadius: 4,
  fontSize: '0.875rem',
  background: '#fff',
  boxSizing: 'border-box',
};

export default function SearchableSelect({
  items = [],
  selected = null,
  onSelect,
  disabled = false,
  placeholder = '🔍 Rechercher...',
  getKey = (it) => it.id,
  getPrimary = (it) => it.name || '',
  getSecondary = (it) => '',
  searchFields = [(it) => it.name || ''],
  maxResults = 8,
  autoFocus = false,
}) {
  const [query, setQuery]   = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIdx, setIdx] = useState(0);
  const containerRef        = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    window.addEventListener('click', onClick);
    return () => window.removeEventListener('click', onClick);
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = !q
    ? items.slice(0, maxResults)
    : items.filter(it => {
        return searchFields.some(fn => {
          const val = fn(it) || '';
          return val.toString().toLowerCase().includes(q);
        });
      }).slice(0, maxResults);

  function handleSelect(item) {
    onSelect(item);
    setQuery('');
    setIsOpen(false);
    setIdx(0);
  }

  function handleKeyDown(e) {
    if (!isOpen) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIdx(i => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIdx(i => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[activeIdx]) handleSelect(filtered[activeIdx]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {selected && !isOpen ? (
        <div
          style={{
            padding: '0.5rem 0.625rem',
            border: '1px solid #d1d5db',
            borderRadius: 4,
            background: disabled ? '#f9fafb' : '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
          onClick={() => !disabled && setIsOpen(true)}
        >
          <div>
            <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#111827' }}>
              {getPrimary(selected)}
            </div>
            {getSecondary(selected) && (
              <div style={{ fontSize: '0.6875rem', color: '#9ca3af', fontFamily: 'monospace' }}>
                {getSecondary(selected)}
              </div>
            )}
          </div>
          {!disabled && (
            <button
              type="button"
              style={{ background: 'transparent', border: 0, color: '#9ca3af', cursor: 'pointer', fontSize: '1rem', padding: '0 0.25rem' }}
              onClick={e => { e.stopPropagation(); onSelect(null); setQuery(''); setIsOpen(true); }}
              title="Désélectionner"
            >×</button>
          )}
        </div>
      ) : (
        <input
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setIsOpen(true); setIdx(0); }}
          onFocus={() => !disabled && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          style={inputStyle}
          disabled={disabled}
          autoFocus={autoFocus && !selected}
        />
      )}

      {isOpen && !selected && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          background: '#fff',
          border: '1px solid #d1d5db',
          borderRadius: 4,
          marginTop: 2,
          maxHeight: 280,
          overflowY: 'auto',
          zIndex: 10,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        }}>
          {filtered.length === 0 && (
            <div style={{ padding: '0.75rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.8125rem' }}>
              Aucun résultat
            </div>
          )}
          {filtered.map((it, idx) => (
            <div
              key={getKey(it)}
              onClick={() => handleSelect(it)}
              onMouseEnter={() => setIdx(idx)}
              style={{
                padding: '0.5rem 0.625rem',
                cursor: 'pointer',
                background: idx === activeIdx ? '#dbeafe' : 'transparent',
                borderBottom: idx < filtered.length - 1 ? '1px solid #f3f4f6' : 'none',
              }}
            >
              <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#111827' }}>
                {getPrimary(it)}
              </div>
              {getSecondary(it) && (
                <div style={{ fontSize: '0.6875rem', color: '#6b7280', fontFamily: 'monospace' }}>
                  {getSecondary(it)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
